import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";

import useUpdateItem from "../../../api/api-hooks/workspaces/sidebar/useUpdateItem";
import CloudAppDrawer, {
  CloudAppDrawerType,
} from "../../Drawers/CloudAppDrawer";
import AddExistingPageInCloudApp from "../../Drawers/AddExistingPageInCloudApp";
import useWorkspace from "../../../api/api-hooks/workspaces/useWorkspace";
import { NestedSortable } from "../../NestedSortable/NestedSortable";
import { TreeItem, TreeItems } from "../../NestedSortable/types";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import { FileInterface } from "../../../types/RecordInterfaces";
import { pageSettingsAtom } from "../../../atoms/PageAtoms";
import ForceUpdateAppDrawer from "../ForceUpdateAppDrawer";
import useUser from "../../../api/api-hooks/auth/useUser";
import WorkspaceAPI from "../../../api/api/WorkspaceAPI";
import { newToastEvent } from "../../../utils/ToastUtil";
import AddPageDrawer from "../../Drawers/AddPageDrawer";
import Constants from "../../../utils/Constants";
import StrUtils from "../../../utils/StrUtils";
import RenameModal from "../RenameModal";

export const createEmptyLink = (): TreeItem => {
  const tempId = "Empty" + StrUtils.uuidv4();

  return {
    name: "Empty",
    id: tempId,
    children: [],
    collapsed: true,
    data: { id: tempId, Title: "Empty", Type: "Empty" },
  };
};

interface Props {
  sidebarItems: SidebarItem[];
  reorderEnabled?: boolean;
  appsVisibility?: Record<string, boolean>;
}

export type SidebarItem = {
  id: string;
  Title: string;
  Description?: string;
} & (
  | {
      Type: "PageLink";
      WorkspaceID: string;
      PageID: string;
      Privacy: "public" | "private";
      IconURL?: string;
      AppID?: string;
      GlobalAppID?: string;
      isApp?: boolean;
    }
  | {
      Type: "CloudAppViewer" | "CloudAppOwner";
      GlobalAppID?: string;
      GlobalAppVersion?: number;
      DefaultPage?: string;
      Items: SidebarItem[];
      Icon?: string | FileInterface;
    }
  | { Type: "Group"; Items: SidebarItem[] }
  | { Type: "External"; Link: string }
  | { Type: "Direct" | "Empty" | "EmptySidebar" }
  | { Type: "ModuleLink"; ModuleAPIName: string }
);

// Convert sidebar items to a tree structure
/**
 * @param {*} sidebarItems Sidebar items from the workspace
 * @param {*} appsVisibility Contains information about the apps that should be visible in the sidebar.
 */
const convertItemsToTreeItems = (
  sidebarItems: SidebarItem[],
  appsVisibility?: Record<string, boolean>
): TreeItems => {
  return sidebarItems.flatMap((item) => {
    const newTreeItem: TreeItem = {
      id: item.id,
      name: item.Title,
      children: [],
      collapsed: true,
      data: item,
    };

    if (
      item?.Type === "Group" ||
      item?.Type === "CloudAppViewer" ||
      item?.Type === "CloudAppOwner"
    ) {
      // @ts-ignore
      if (appsVisibility && appsVisibility[item?.GlobalAppID] === false)
        return [];

      if (!item?.Items?.length) {
        newTreeItem.children = [createEmptyLink()];
      } else {
        newTreeItem.children = item?.Items?.map((item) => {
          return {
            id: item.id,
            name: item.Title,
            children: [],
            data: item,
          };
        });
      }
    }

    return [newTreeItem];
  });
};

export default function CustomLinks2({
  sidebarItems,
  reorderEnabled = true,
  appsVisibility,
}: Props) {
  const navigate = useNavigate();
  const { tenantID } = useParams();
  const workspaceID = useAtomValue(workspaceIdAtom);
  const { updateItem } = useUpdateItem();

  const [editingItem, setEditingItem] = useState<TreeItem | null>(null);
  const [addPage, setAddPage] = useState<string>("");
  const setSelectedPage = useSetAtom(pageSettingsAtom);
  const [cloudAppDrawerOpened, setCloudAppDrawerOpened] = useState(false);
  const [cloudAppDrawerType, setCloudAppDrawerType] =
    useState<CloudAppDrawerType>();
  const [cloudAppFolder, setCloudAppFolder] = useState(null);
  const [existingCloudApp, setExistingCloudApp] = useState(null);
  const [addExistingPageTo, setExistingPageTo] = useState(null);
  const [forceUpdateDrawer, setForceUpdateDrawer] = useState(null);
  const [items, setItems] = useState<TreeItems>([]);
  const client = useQueryClient();
  const { workspaceLoading } = useWorkspace();

  const { user } = useUser();

  const isAdmin = user?.Tenants?.find((t: any) =>
    [Constants.MC_CLOUD_ID, Constants.SUP_CLOUD_ID].includes(t.TenantID)
  );

  // Sync the items with the state
  useEffect(() => {
    setItems(convertItemsToTreeItems(sidebarItems, appsVisibility));
  }, [sidebarItems, appsVisibility]);

  if (workspaceLoading) {
    return Array(6)
      .fill(undefined)
      .map((_, i) => (
        <div
          key={i}
          style={{ height: 30 }}
          className="skeleton tw:mx-3 tw:my-1"
        ></div>
      ));
  }

  return (
    <div className="tw:overflow-y-auto tw:flex-1">
      <div className="tw:mx-3 tw:pb-4">
        <NestedSortable
          items={items}
          setItems={setItems}
          onOrderChange={handleSaveTree}
          indicator={true}
          collapsible={true}
          reorderDisabled={!reorderEnabled}
          onCollapse={onCollapse}
          setEditingItem={setEditingItem}
          setAddPage={setAddPage}
          setSelectedPage={setSelectedPage}
          setCloudAppDrawerOpened={setCloudAppDrawerOpened}
          setCloudAppDrawerType={setCloudAppDrawerType}
          setCloudAppFolder={setCloudAppFolder}
          setExistingCloudApp={setExistingCloudApp}
          setExistingPageTo={setExistingPageTo}
          setForceUpdateDrawer={setForceUpdateDrawer}
        />
      </div>
      <RenameModal
        open={!!editingItem}
        handleClose={() => setEditingItem(null)}
        item={editingItem}
        onSave={handleRename}
      />
      <AddPageDrawer
        open={!!addPage}
        handleClose={() => setAddPage("")}
        folderID={addPage}
      />
      <CloudAppDrawer
        open={cloudAppDrawerOpened}
        onClose={handleCloseCloudAppDrawer}
        folder={cloudAppFolder}
        existingCloudApp={existingCloudApp as any}
        type={cloudAppDrawerType}
      />
      <AddExistingPageInCloudApp
        open={!!addExistingPageTo}
        appItem={addExistingPageTo}
        handleClose={() => {
          setExistingPageTo(null);
        }}
      />
      {isAdmin && (
        <ForceUpdateAppDrawer
          open={!!forceUpdateDrawer}
          onClose={() => setForceUpdateDrawer(null)}
          appItem={forceUpdateDrawer}
        />
      )}
    </div>
  );

  // Renaming a sidebar item
  function handleRename(newName: string, itemId: string) {
    const newTree = [...items];

    const itemToUpdate = newTree.find(({ id }) => id === itemId);

    if (!itemToUpdate || !itemToUpdate.data) return;

    itemToUpdate.data.Title = newName;

    setItems(newTree);
    updateItem({ newName, itemId });
  }

  function handleCloseCloudAppDrawer() {
    setCloudAppDrawerOpened(false);
    setExistingCloudApp(null);
    setCloudAppDrawerType(undefined);
    setCloudAppFolder(null);
  }

  // Check if the item opened is a Cloud App
  // If so, navigate to the app's default page (if it exists)
  function onCollapse(id: string, action: "open" | "close") {
    if (action === "close") return;

    const sidebarItem = items.find((i) => i.id === id);

    if (!sidebarItem?.data) return;

    // For Global Apps, navigate to the app's default page
    if ("DefaultPage" in sidebarItem?.data && sidebarItem.data.DefaultPage) {
      const defaultPage = sidebarItem.data.DefaultPage;

      // Don't navigate to the default page if it's already open
      if (window.location.pathname.includes(defaultPage)) return;

      navigate(
        `/clouds/${tenantID}/workspaces/${workspaceID}/pages/${defaultPage}/views/None`
      );
    }
  }

  // Save the new sidebar to the workspace
  // If an app is updated, we need to pass the updated app's ID to the API.
  async function handleSaveTree(
    newItems: TreeItems,
    appUpdated: string | null
  ) {
    let sidebarItems: SidebarItem[] = [];

    // Return tree back to original workspace.Sidebar format.
    sidebarItems = newItems.flatMap((item) => {
      if (!item.data) return [];

      const sidebarItem: SidebarItem = structuredClone(item.data);

      // Remove temporary "Empty" items
      if (item.children?.length && "Items" in sidebarItem) {
        sidebarItem.Items = item.children.flatMap((child) => {
          if (child.data?.Type === "Empty") return [];
          if (child.data?.Type === "EmptySidebar") return [];

          return child.data ? [{ ...child.data }] : [];
        });
      }

      return sidebarItem;
    });

    try {
      await WorkspaceAPI.updateWorkspace(workspaceID, {
        Sidebar: sidebarItems,
        ...(!!appUpdated ? { AppUpdated: appUpdated } : {}),
      });
    } catch {
      newToastEvent("error", "Failed to save Sidebar.");
    }

    // Update the Workspace Cache State
    client.setQueryData<any>(["workspace", workspaceID], (prev: any) => {
      if (!prev?.workspace) return prev;

      return {
        ...prev,
        workspace: { ...prev.workspace, Sidebar: sidebarItems },
      };
    });
  }
}
